import { Box, Typography } from "@mui/material";
import DataGridComponent from "../components/datagrid/datagrid.component";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  companyColumns,
  createRowsData,
} from "../config/tables/usersTable.config";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsersStart } from "../store/features/users/usersSlice";
import { useNavigate } from "react-router";
import { getRouterPaths } from "../router/routerPaths";
import CustomDialog from "../components/muiDialogs/dialog.component";
import { dialogMessages } from "../components/forms/utils";
import { getCompanyActions } from "../utils/userUtils";
import {
  deleteUserStart,
  blockUserStart,
  resetBlockUserState,
  resetUpdateUserState,
  resetDeleteUserState,
} from "../store/features/user/userSlice";
import { useIsMobile } from "../hook/useIsMobile";
import { useBreakpoint } from "../hook/useBreakpoint";

const UsersPage = () => {
  const isMobile = useIsMobile();
  const breakpoint = useBreakpoint();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [userCount, setUserCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const searchTimeoutRef = useRef();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAction, setSelectedAction] = useState("");

  const accessToken = useSelector((state) => state.auth?.user?.access_token);
  const type = useSelector((state) => state?.me?.data?.data?.type);
  const usersData = useSelector((state) => state.users?.data?.data);
  const isUsersLoading = useSelector((state) => state.users?.isLoading);
  const { success } = useSelector((state) => state.user);

  const routerPaths = getRouterPaths(type);
  const rows = usersData?.map(createRowsData);

  const companyActions = getCompanyActions({
    navigate,
    routerPaths,
    setSelectedUser,
    setSelectedAction,
    setDialogOpen,
  });

  const handleSearchChange = useCallback((value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 500);
  }, []);

  const handleDialogConfirm = () => {
    if (selectedUser) {
      if (selectedAction === "block" || selectedAction === "unblock") {
        dispatch(blockUserStart({ userId: selectedUser, accessToken }));
      } else if (selectedAction === "delete") {
        dispatch(deleteUserStart({ userId: selectedUser, accessToken }));
      }
    }
    setDialogOpen(false);
    setSelectedUser(null);
    setSelectedAction("");
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setSelectedAction("");
  };

  useEffect(() => {
    if (type === "Admin") {
      dispatch(fetchUsersStart({ accessToken, search: searchQuery }));
    }
  }, [accessToken, dispatch, type, searchQuery]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (success) {
      dispatch(resetBlockUserState());
      dispatch(resetUpdateUserState());
      dispatch(resetDeleteUserState());
    }
  }, [dispatch, success]);

  return (
    <Box width="100%">
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "16px",
            color: "#666666",
          }}
        >
          {userCount} utenti
        </Typography>
      </Box>
      <DataGridComponent
        onRowCountChange={setUserCount}
        rows={rows}
        columns={companyColumns(companyActions, breakpoint)}
        buttonHref={`/${type?.toLowerCase()}/users/addUser`}
        buttonLabel={"Nuovo utente"}
        showButton
        inputPlaceholder="Cerca nome azienda o email"
        isMobile={isMobile}
        onSearchChange={handleSearchChange}
        loading={isUsersLoading}
      />
      <CustomDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        title={dialogMessages.user[selectedAction]?.title}
        description={dialogMessages.user[selectedAction]?.description}
        confirmLabel={dialogMessages.user[selectedAction]?.confirmLabel}
        actions={companyActions}
      />
    </Box>
  );
};

export default UsersPage;
